import { CookieConsentContextType } from '@typing/State';

/* eslint-disable @typescript-eslint/naming-convention */
export type Cookie = {
  cookie: string;
  description: string;
  domain: string;
  isRegex?: boolean;
  vendor: string;
};

export enum CookieCategory {
  ANALYTICS = 'ANALYTICS',
  FUNCTIONAL = 'FUNCTIONAL',
  NECESSARY = 'NECESSARY',
  PAYMENT = 'PAYMENT'
}

type CookieCallbackData = {
  categories: string[];
};

const toCookieBannerFormat = (cookies: Cookie[]) =>
  cookies.map(cookie => ({
    col1: cookie.cookie,
    col2: cookie.domain,
    col3: cookie.vendor,
    is_regex: cookie.isRegex
  }));

export const necessaryCookiesAllowed = () => window.CookieConsentApi.allowedCategory('necessary');
export const functionalCookiesAllowed = () => window.CookieConsentApi.allowedCategory('functional');
export const analyticsCookiesAllowed = () => window.CookieConsentApi.allowedCategory('analytics');
export const paymentCookiesAllowed = () => window.CookieConsentApi.allowedCategory('payment');

export const necessaryCookies: Cookie[] = [
  {
    cookie: '_grecaptcha',
    description: 'Google reCAPTCHA status, used to verify users are not bots',
    domain: 'google.com',
    vendor: 'Google reCAPTCHA'
  },
  {
    cookie: 'cc_cookie',
    description: 'Information about your acceptance of cookies',
    domain: 'withgrayce.com',
    vendor: 'Grayce'
  }
];

export const functionalCookies: Cookie[] = [
  {
    cookie: '_delighted_web',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'withgrayce.com',
    vendor: 'Delighted'
  },
  {
    cookie: 'do',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    vendor: 'Delighted'
  },
  {
    cookie: 'intercom-*',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    isRegex: true,
    vendor: 'Delighted'
  },
  {
    cookie: 'lid',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    vendor: 'Delighted'
  },
  {
    cookie: 'plhi',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    vendor: 'Delighted'
  },
  {
    cookie: 'ms',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    vendor: 'Delighted'
  },
  {
    cookie: 'avc',
    description: 'Delighted is used to survey users, this stores information about your survey history',
    domain: 'delighted.com',
    vendor: 'Delighted'
  },
  {
    cookie: '__kla_id',
    description: 'Stores information about the user for Klaviyo',
    domain: 'klaviyo.com',
    vendor: 'Klaviyo'
  },
  {
    cookie: 'kl_sessionid',
    description: 'Klaviyo identifier for forms',
    domain: 'klaviyo.com',
    vendor: 'Klaviyo'
  },
  {
    cookie: 'kl_csrftoken',
    description: 'Security token for Klaviyo forms',
    domain: 'klaviyo.com',
    vendor: 'Klaviyo'
  }
];

export const analyticsCookies: Cookie[] = [
  {
    cookie: 'fs_lua',
    description: 'Stores information about the user for FullStory',
    domain: 'withgrayce.com',
    vendor: 'Fullstory'
  },
  {
    cookie: 'fs_uid',
    description: 'Stores information about the user for FullStory',
    domain: 'fullstory.com',
    vendor: 'Fullstory'
  },
  {
    cookie: '__q_state_*',
    description: 'Stores information about the user for FullStory',
    domain: 'fullstory.com',
    isRegex: true,
    vendor: 'Fullstory'
  },
  {
    cookie: '_ga*',
    description: 'Stores information about the user for Google analytics',
    domain: 'withgrayce.com',
    isRegex: true,
    vendor: 'Google'
  },
  {
    cookie: '_gid',
    description: 'Stores information about the user for Google analytics',
    domain: 'google.com',
    vendor: 'Google'
  },
  {
    cookie: '__kla_id',
    description: 'Stores information about the user for Klaviyo',
    domain: 'withgrayce.com',
    vendor: 'Klaviyo'
  },
  {
    cookie: '__kla_off',
    description: 'Stores the state of disabling Klaviyo via the cookie policy',
    domain: 'withgrayce.com',
    vendor: 'Klaviyo'
  }
];

export const paymentCookies: Cookie[] = [
  {
    cookie: 'stripe.csrf',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'stripe.com',
    vendor: 'Stripe'
  },
  {
    cookie: '__stripe_orig_props',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'stripe.com',
    vendor: 'Stripe'
  },
  {
    cookie: '__stripe_sid',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'withgrayce.com',
    vendor: 'Stripe'
  },
  {
    cookie: '__stripe_mid',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'withgrayce.com',
    vendor: 'Stripe'
  },
  {
    cookie: 'machine_identifier',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'stripe.com',
    vendor: 'Stripe'
  },
  {
    cookie: 'private_machine_identifier',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'stripe.com',
    vendor: 'Stripe'
  },
  {
    cookie: 'cookie-perms',
    description: 'Used to prevent forgery when making payments with Stripe',
    domain: 'stripe.com',
    vendor: 'Stripe'
  }
];

/* eslint-disable @typescript-eslint/naming-convention */
export const initializeCookieConsentBanner = (onAcceptanceChange: (status: CookieConsentContextType) => void) => {
  if (!document.getElementById('cc--main')) {
    window.CookieConsentApi = window.initCookieConsent();
    window.CookieConsentApi.run({
      autoclear_cookies: true,
      current_lang: 'en',

      // mode: 'opt-in'                          // default: 'opt-in'; value: 'opt-in' or 'opt-out'
      // delay: 0,                               // default: 0
      // auto_language: null                     // default: null; could also be 'browser' or 'document'
      // autorun: true,                          // default: true
      // force_consent: false,                   // default: false
      // hide_from_bots: true,                   // default: true
      // remove_cookie_tables: false             // default: false
      // cookie_name: 'cc_cookie',               // default: 'cc_cookie'
      // cookie_expiration: 182,                 // default: 182 (days)
      // cookie_necessary_only_expiration: 182   // default: disabled
      // cookie_domain: location.hostname,       // default: current domain
      // cookie_path: '/',                       // default: root
      // cookie_same_site: 'Lax',                // default: 'Lax'
      // use_rfc_cookie: false,                  // default: false
      // revision: 0,                            // default: 0

      languages: {
        en: {
          consent_modal: {
            description:
              'We use cookies to personalize our website and offerings to your interests and for measurement and analytics purposes. By using our website and our products, you agree to our use of cookies. <button type="button" data-cc="c-settings" class="cc-link">Let me choose.</button>',
            primary_btn: {
              role: 'accept_all',
              text: 'Accept all'
            },
            secondary_btn: {
              role: 'accept_necessary',
              text: 'Reject all'
            },
            title: 'Grayce uses cookies'
          },
          settings_modal: {
            accept_all_btn: 'Accept all',
            blocks: [
              {
                description:
                  'Grayce uses cookies to ensure the basic functionality of our website and to enhance your online experience. For each of the following categories, you can choose to opt-in or out whenever you want. For further details about cookies and other sensitive data, please read the full <a href="https://withgrayce.com/privacy-policy/" class="cc-link">privacy</a> and <a href="/cookie-policy/" class="cc-link">cookie</a> policies.',
                title: 'Cookie usage'
              },
              {
                cookie_table: toCookieBannerFormat(necessaryCookies),
                description: 'These cookies are necessary for the functioning of our service',
                title: 'Necessary',
                toggle: {
                  enabled: true,
                  readonly: true,
                  value: CookieCategory.NECESSARY
                }
              },
              {
                cookie_table: toCookieBannerFormat(functionalCookies),
                description:
                  'Functionality cookies are used to keep our site and services working correctly, like showing you the right information for your selected location or remembering your preferences.',
                title: 'Functionality',
                toggle: {
                  enabled: true,
                  readonly: false,
                  value: CookieCategory.FUNCTIONAL
                }
              },

              {
                cookie_table: toCookieBannerFormat(analyticsCookies),
                description:
                  'Analytics cookies help us understand how visitors interact with our Services, allowing us to analyze and improve our Services (also through third party analytics).',
                title: 'Analytics',
                toggle: {
                  enabled: true,
                  readonly: false,
                  value: CookieCategory.ANALYTICS
                }
              },
              {
                cookie_table: toCookieBannerFormat(paymentCookies),
                description:
                  'These cookies help prevent fraud in payment processing and are required when making payments to Grayce',
                title: 'Payment fraud protection cookies',
                toggle: {
                  enabled: true,
                  readonly: false,
                  value: CookieCategory.PAYMENT
                }
              },
              {
                description:
                  'For any queries in relation to our policy on cookies and your choices, please <a class="cc-link" href="mailto:support@withgrayce.com">contact us</a>.',
                title: 'More information'
              }
            ],
            close_btn_label: 'Close',
            cookie_table_headers: [{ col1: 'Name' }, { col2: 'Domain' }, { col3: 'Origin' }],
            reject_all_btn: 'Reject all',
            save_settings_btn: 'Save settings',
            title: 'Cookie preferences'
          }
        }
      },

      onAccept: function (cookieData: CookieCallbackData) {
        onAcceptanceChange({
          acceptsAnalyticsCookies: cookieData.categories.includes(CookieCategory.ANALYTICS),
          acceptsFunctionalCookies: cookieData.categories.includes(CookieCategory.FUNCTIONAL),
          acceptsNecessaryCookies: cookieData.categories.includes(CookieCategory.NECESSARY),
          acceptsPaymentCookies: cookieData.categories.includes(CookieCategory.PAYMENT)
        });
      },

      onChange: function (cookieData: CookieCallbackData, _changedPreferences: any) {
        onAcceptanceChange({
          acceptsAnalyticsCookies: cookieData.categories.includes(CookieCategory.ANALYTICS),
          acceptsFunctionalCookies: cookieData.categories.includes(CookieCategory.FUNCTIONAL),
          acceptsNecessaryCookies: cookieData.categories.includes(CookieCategory.NECESSARY),
          acceptsPaymentCookies: cookieData.categories.includes(CookieCategory.PAYMENT)
        });
      },

      onFirstAction: function (_userPreferences: any, cookieData: CookieCallbackData) {
        onAcceptanceChange({
          acceptsAnalyticsCookies: cookieData.categories.includes(CookieCategory.ANALYTICS),
          acceptsFunctionalCookies: cookieData.categories.includes(CookieCategory.FUNCTIONAL),
          acceptsNecessaryCookies: cookieData.categories.includes(CookieCategory.NECESSARY),
          acceptsPaymentCookies: cookieData.categories.includes(CookieCategory.PAYMENT)
        });
      },

      page_scripts: false
    });
  }
};

export const displayCookieConsentBanner = () => {
  window.CookieConsentApi.showSettings();
};
